import { useMemo, useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Store, Users, ClipboardList, DollarSign, TrendingUp,
  ArrowUpRight, ArrowDownRight, Activity, Zap, Clock,
  Crown, Flame, CheckCircle2, XCircle, Moon, Eye,
  ShoppingBag, Percent, BarChart3, Sparkles, Circle
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell
} from "recharts";

interface Props {
  restaurants: any[];
  orders: any[];
  profiles: any[];
  subscriptions: any[];
}

const formatCurrency = (v: number) =>
  `R$ ${v.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const tooltipStyle = {
  background: "hsl(var(--card))",
  border: "1px solid hsl(var(--border))",
  borderRadius: "12px",
  fontSize: "11px",
  color: "hsl(var(--foreground))",
  boxShadow: "0 8px 32px -8px hsl(var(--foreground) / 0.1)",
  padding: "8px 12px",
};

function isRestaurantOpen(r: any): boolean {
  if (r.is_manually_closed) return false;
  if (!r.opening_time || !r.closing_time) return true;
  const now = new Date();
  const dayMap: Record<number, string> = { 0: "dom", 1: "seg", 2: "ter", 3: "qua", 4: "qui", 5: "sex", 6: "sab" };
  const todayKey = dayMap[now.getDay()];
  const operatingDays = r.operating_days as string[] | null;
  if (operatingDays && !operatingDays.includes(todayKey)) return false;
  const [oh, om] = r.opening_time.split(":").map(Number);
  const [ch, cm] = r.closing_time.split(":").map(Number);
  const nowMin = now.getHours() * 60 + now.getMinutes();
  const openMin = oh * 60 + (om || 0);
  const closeMin = ch * 60 + (cm || 0);
  if (closeMin > openMin) return nowMin >= openMin && nowMin < closeMin;
  return nowMin >= openMin || nowMin < closeMin;
}

const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.06 } } };
const fadeUp = { hidden: { opacity: 0, y: 16 }, visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" as const } } };

const AdminOverviewTab = ({ restaurants, orders, profiles, subscriptions }: Props) => {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => setTick(t => t + 1), 30000);
    return () => clearInterval(interval);
  }, []);

  const [rankingPeriod, setRankingPeriod] = useState<"7d" | "30d" | "all">("30d");
  const activeRestaurants = restaurants.filter((r) => r.is_active).length;
  const totalRevenue = orders.reduce((s: number, o: any) => s + Number(o.total), 0);
  const today = new Date().toISOString().slice(0, 10);
  const todayOrders = orders.filter((o: any) => o.created_at.slice(0, 10) === today);
  const todayRevenue = todayOrders.reduce((s: number, o: any) => s + Number(o.total), 0);
  const activeSubscriptions = subscriptions.filter((s: any) => s.status === "active").length;
  const mrr = subscriptions.filter((s: any) => s.status === "active").reduce((sum: number, s: any) => sum + Number(s.amount), 0);

  const openRestaurants = useMemo(() => restaurants.filter(r => r.is_active && isRestaurantOpen(r)), [restaurants, tick]);
  const closedRestaurants = useMemo(() => restaurants.filter(r => r.is_active && !isRestaurantOpen(r)), [restaurants, tick]);

  const restaurantRanking = useMemo(() => {
    const now = new Date();
    const cutoff = rankingPeriod === "7d"
      ? new Date(now.getTime() - 7 * 86400000).toISOString()
      : rankingPeriod === "30d"
        ? new Date(now.getTime() - 30 * 86400000).toISOString()
        : null;
    const filteredOrders = cutoff ? orders.filter((o: any) => o.created_at >= cutoff) : orders;
    const map: Record<string, { name: string; slug: string; revenue: number; orderCount: number; todayOrders: number; type: string; isOpen: boolean }> = {};
    restaurants.forEach(r => {
      map[r.id] = { name: r.name, slug: r.slug, revenue: 0, orderCount: 0, todayOrders: 0, type: r.type || "restaurante", isOpen: r.is_active && isRestaurantOpen(r) };
    });
    filteredOrders.forEach((o: any) => {
      if (map[o.restaurant_id]) {
        map[o.restaurant_id].revenue += Number(o.total);
        map[o.restaurant_id].orderCount += 1;
        if (o.created_at.slice(0, 10) === today) map[o.restaurant_id].todayOrders += 1;
      }
    });
    return Object.values(map).sort((a, b) => b.revenue - a.revenue).slice(0, 15);
  }, [restaurants, orders, today, rankingPeriod, tick]);

  const revenueChartData = useMemo(() => {
    const days: { date: string; label: string; revenue: number; orders: number }[] = [];
    for (let i = 6; i >= 0; i--) {
      const d = new Date(); d.setDate(d.getDate() - i);
      const iso = d.toISOString().slice(0, 10);
      const dayOrders = orders.filter((o: any) => o.created_at.slice(0, 10) === iso);
      days.push({ date: iso, label: d.toLocaleDateString("pt-BR", { weekday: "short" }).replace(".", ""), revenue: dayOrders.reduce((s: number, o: any) => s + Number(o.total), 0), orders: dayOrders.length });
    }
    return days;
  }, [orders]);

  const ordersByStatus = useMemo(() => {
    const statusMap: Record<string, number> = {};
    orders.forEach((o: any) => { statusMap[o.status] = (statusMap[o.status] || 0) + 1; });
    return Object.entries(statusMap).map(([name, value]) => ({ name, value }));
  }, [orders]);

  const PIE_COLORS = ["hsl(24, 95%, 53%)", "hsl(142, 71%, 45%)", "hsl(38, 92%, 50%)", "hsl(0, 72%, 51%)", "hsl(200, 80%, 50%)", "hsl(280, 60%, 50%)"];
  const STATUS_LABELS: Record<string, string> = { received: "Recebido", preparing: "Preparando", ready: "Pronto", out_for_delivery: "Entregando", completed: "Concluído", cancelled: "Cancelado" };

  const thisWeekRestaurants = useMemo(() => {
    const weekAgo = new Date(); weekAgo.setDate(weekAgo.getDate() - 7);
    return restaurants.filter(r => new Date(r.created_at) >= weekAgo).length;
  }, [restaurants]);
  const lastWeekRestaurants = useMemo(() => {
    const twoWeeksAgo = new Date(); twoWeeksAgo.setDate(twoWeeksAgo.getDate() - 14);
    const weekAgo = new Date(); weekAgo.setDate(weekAgo.getDate() - 7);
    return restaurants.filter(r => { const d = new Date(r.created_at); return d >= twoWeeksAgo && d < weekAgo; }).length;
  }, [restaurants]);
  const restGrowth = lastWeekRestaurants > 0
    ? (((thisWeekRestaurants - lastWeekRestaurants) / lastWeekRestaurants) * 100).toFixed(0)
    : thisWeekRestaurants > 0 ? "+100" : "0";
  const ticketMedio = orders.length > 0 ? totalRevenue / orders.length : 0;

  const MEDAL_COLORS = ["hsl(38, 92%, 50%)", "hsl(0, 0%, 70%)", "hsl(25, 60%, 45%)"];
  const recentOrders = orders.slice(0, 8);

  const heroStats = [
    {
      label: "Restaurantes",
      value: restaurants.length,
      sub: `${activeRestaurants} ativos`,
      icon: Store,
      trend: `${Number(restGrowth) >= 0 ? "+" : ""}${restGrowth}%`,
      trendUp: Number(restGrowth) >= 0,
      color: "from-orange-500/20 to-amber-500/10",
      iconBg: "bg-orange-500/15 text-orange-500",
    },
    {
      label: "Usuários",
      value: profiles.length.toLocaleString("pt-BR"),
      sub: "cadastrados",
      icon: Users,
      trend: null,
      trendUp: true,
      color: "from-blue-500/20 to-cyan-500/10",
      iconBg: "bg-blue-500/15 text-blue-500",
    },
    {
      label: "Pedidos",
      value: orders.length.toLocaleString("pt-BR"),
      sub: `${todayOrders.length} hoje`,
      icon: ShoppingBag,
      trend: null,
      trendUp: true,
      color: "from-emerald-500/20 to-green-500/10",
      iconBg: "bg-emerald-500/15 text-emerald-500",
    },
    {
      label: "Faturamento Total",
      value: formatCurrency(totalRevenue),
      sub: `${formatCurrency(todayRevenue)} hoje`,
      icon: DollarSign,
      trend: null,
      trendUp: true,
      color: "from-violet-500/20 to-purple-500/10",
      iconBg: "bg-violet-500/15 text-violet-500",
    },
  ];

  const kpiCards = [
    { label: "MRR", value: formatCurrency(mrr), sub: "Receita recorrente mensal", icon: Zap, accent: "text-amber-500" },
    { label: "Assinaturas Ativas", value: activeSubscriptions, sub: `de ${subscriptions.length} total`, icon: TrendingUp, accent: "text-emerald-500" },
    { label: "Ticket Médio", value: formatCurrency(ticketMedio), sub: "por pedido", icon: BarChart3, accent: "text-blue-500" },
    { label: "Taxa de Conversão", value: `${restaurants.length > 0 ? ((activeSubscriptions / restaurants.length) * 100).toFixed(0) : 0}%`, sub: "restaurantes com assinatura", icon: Percent, accent: "text-violet-500" },
  ];

  return (
    <motion.div className="space-y-6" variants={stagger} initial="hidden" animate="visible">

      {/* ─── Hero Stats ─── */}
      <motion.div variants={fadeUp} className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {heroStats.map((s, i) => (
          <div
            key={s.label}
            className={`relative overflow-hidden rounded-2xl border border-border/60 bg-gradient-to-br ${s.color} p-5 group hover:border-primary/20 transition-all duration-300`}
          >
            {/* Decorative circle */}
            <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full bg-foreground/[0.02] group-hover:scale-125 transition-transform duration-500" />
            
            <div className="flex items-start justify-between mb-4">
              <div className={`w-10 h-10 rounded-xl ${s.iconBg} flex items-center justify-center`}>
                <s.icon className="h-5 w-5" />
              </div>
              {s.trend && (
                <span className={`flex items-center gap-0.5 text-[11px] font-bold px-2 py-0.5 rounded-full backdrop-blur-sm ${
                  s.trendUp ? "bg-emerald-500/10 text-emerald-500" : "bg-red-500/10 text-red-500"
                }`}>
                  {s.trendUp ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
                  {s.trend}
                </span>
              )}
            </div>
            <p className="text-2xl font-bold font-display leading-none tracking-tight">{s.value}</p>
            <p className="text-xs text-muted-foreground mt-1.5 font-medium">{s.label}</p>
            <p className="text-[11px] text-muted-foreground/70 mt-0.5">{s.sub}</p>
          </div>
        ))}
      </motion.div>

      {/* ─── Live Restaurant Status ─── */}
      <motion.div variants={fadeUp} className="rounded-2xl border border-border/60 bg-card overflow-hidden">
        <div className="p-5 pb-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse" />
            <div>
              <h3 className="font-display font-bold text-sm">Restaurantes em Tempo Real</h3>
              <p className="text-[11px] text-muted-foreground mt-0.5">
                <span className="text-emerald-500 font-semibold">{openRestaurants.length} abertos</span>
                {" · "}
                <span>{closedRestaurants.length} fechados</span>
                {" · "}
                <span>{restaurants.length - activeRestaurants} inativos</span>
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4 text-[11px]">
            <span className="flex items-center gap-1.5 text-emerald-500 font-medium">
              <Circle className="h-2 w-2 fill-emerald-500" /> Aberto
            </span>
            <span className="flex items-center gap-1.5 text-muted-foreground">
              <Circle className="h-2 w-2 fill-muted-foreground/40" /> Fechado
            </span>
          </div>
        </div>

        {restaurants.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground text-xs">Nenhum restaurante cadastrado</div>
        ) : (
          <div className="px-5 pb-5 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2.5">
            {restaurants
              .filter(r => r.is_active)
              .sort((a, b) => (isRestaurantOpen(b) ? 1 : 0) - (isRestaurantOpen(a) ? 1 : 0))
              .map((r) => {
                const open = isRestaurantOpen(r);
                const rOrders = orders.filter((o: any) => o.restaurant_id === r.id && o.created_at.slice(0, 10) === today);
                return (
                  <div
                    key={r.id}
                    className={`relative rounded-xl border p-3.5 transition-all duration-200 ${
                      open
                        ? "border-emerald-500/20 bg-emerald-500/[0.04] hover:bg-emerald-500/[0.08]"
                        : "border-border/40 bg-muted/20 opacity-50 hover:opacity-70"
                    }`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className={`w-2 h-2 rounded-full mt-0.5 ${open ? "bg-emerald-500 animate-pulse shadow-sm shadow-emerald-500/50" : "bg-muted-foreground/30"}`} />
                      {rOrders.length > 0 && (
                        <Badge variant="outline" className="text-[9px] h-[18px] px-1.5 border-primary/30 text-primary font-bold gap-0.5">
                          <Flame className="h-2.5 w-2.5" />
                          {rOrders.length}
                        </Badge>
                      )}
                    </div>
                    <p className="text-[11px] font-semibold truncate leading-tight">{r.name}</p>
                    <p className="text-[9px] text-muted-foreground truncate font-mono mt-0.5">/{r.slug}</p>
                    <div className="flex items-center gap-1.5 mt-2">
                      {open ? (
                        <CheckCircle2 className="h-3 w-3 text-emerald-500" />
                      ) : r.is_manually_closed ? (
                        <XCircle className="h-3 w-3 text-red-400" />
                      ) : (
                        <Moon className="h-3 w-3 text-muted-foreground" />
                      )}
                      <span className="text-[9px] text-muted-foreground font-medium">
                        {open ? "Aberto" : r.is_manually_closed ? "Fechado manual" : "Fora do horário"}
                      </span>
                    </div>
                  </div>
                );
              })}
          </div>
        )}
      </motion.div>

      {/* ─── Charts Row ─── */}
      <motion.div variants={fadeUp} className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Revenue chart */}
        <div className="lg:col-span-2 rounded-2xl border border-border/60 bg-card p-6">
          <div className="flex items-center justify-between mb-5">
            <div>
              <h3 className="font-display font-bold text-sm">Faturamento — Últimos 7 dias</h3>
              <p className="text-[11px] text-muted-foreground mt-0.5">Receita diária consolidada</p>
            </div>
            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
              <Activity className="h-4 w-4 text-primary" />
            </div>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={revenueChartData}>
              <defs>
                <linearGradient id="revenueGrad2" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="hsl(24, 95%, 53%)" stopOpacity={0.25} />
                  <stop offset="100%" stopColor="hsl(24, 95%, 53%)" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="label" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} dy={8} />
              <YAxis hide />
              <Tooltip contentStyle={tooltipStyle} formatter={(value: number) => [formatCurrency(value), "Receita"]} />
              <Area type="monotone" dataKey="revenue" stroke="hsl(24, 95%, 53%)" strokeWidth={2.5} fill="url(#revenueGrad2)" dot={{ r: 3, fill: "hsl(24, 95%, 53%)", strokeWidth: 0 }} activeDot={{ r: 5, strokeWidth: 2, stroke: "hsl(var(--background))" }} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Orders by status */}
        <div className="rounded-2xl border border-border/60 bg-card p-6">
          <div className="flex items-center justify-between mb-5">
            <div>
              <h3 className="font-display font-bold text-sm">Pedidos por Status</h3>
              <p className="text-[11px] text-muted-foreground mt-0.5">Distribuição atual</p>
            </div>
          </div>
          {ordersByStatus.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-[200px] text-muted-foreground">
              <ClipboardList className="h-8 w-8 mb-2 opacity-20" />
              <p className="text-xs">Sem dados</p>
            </div>
          ) : (
            <div className="flex flex-col items-center">
              <ResponsiveContainer width="100%" height={160}>
                <PieChart>
                  <Pie data={ordersByStatus} cx="50%" cy="50%" innerRadius={45} outerRadius={68} paddingAngle={3} dataKey="value" strokeWidth={0}>
                    {ordersByStatus.map((_, idx) => (
                      <Cell key={idx} fill={PIE_COLORS[idx % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={tooltipStyle} formatter={(value: number, name: string) => [value, STATUS_LABELS[name] || name]} />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex flex-wrap justify-center gap-x-3 gap-y-1.5 mt-2">
                {ordersByStatus.map((entry, idx) => (
                  <div key={entry.name} className="flex items-center gap-1.5 text-[10px] text-muted-foreground font-medium">
                    <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: PIE_COLORS[idx % PIE_COLORS.length] }} />
                    {STATUS_LABELS[entry.name] || entry.name}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </motion.div>

      {/* ─── KPI Cards ─── */}
      <motion.div variants={fadeUp} className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {kpiCards.map((metric) => (
          <div key={metric.label} className="rounded-2xl border border-border/60 bg-card p-5 hover:border-primary/15 transition-all duration-200 group">
            <div className="flex items-center justify-between mb-3">
              <span className="text-[11px] text-muted-foreground font-semibold uppercase tracking-wider">{metric.label}</span>
              <metric.icon className={`h-4 w-4 ${metric.accent} opacity-60 group-hover:opacity-100 transition-opacity`} />
            </div>
            <p className="text-xl font-bold font-display tracking-tight">{metric.value}</p>
            <p className="text-[11px] text-muted-foreground/70 mt-1">{metric.sub}</p>
          </div>
        ))}
      </motion.div>

      {/* ─── Ranking + Orders ─── */}
      <motion.div variants={fadeUp} className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        {/* Restaurant Ranking */}
        <div className="lg:col-span-3 rounded-2xl border border-border/60 bg-card overflow-hidden">
          <div className="p-5 border-b border-border/60 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-amber-500/10 flex items-center justify-center">
                <Crown className="h-4.5 w-4.5 text-amber-500" />
              </div>
              <div>
                <h3 className="font-display font-bold text-sm">Ranking de Restaurantes</h3>
                <p className="text-[11px] text-muted-foreground mt-0.5">Maiores faturamentos do período</p>
              </div>
            </div>
            <div className="flex gap-1 bg-secondary/50 rounded-lg p-0.5">
              {([["7d", "7d"], ["30d", "30d"], ["all", "Todos"]] as const).map(([key, label]) => (
                <button
                  key={key}
                  onClick={() => setRankingPeriod(key)}
                  className={`px-3 py-1.5 rounded-md text-[11px] font-semibold transition-all duration-200 ${
                    rankingPeriod === key
                      ? "bg-primary text-primary-foreground shadow-sm"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          {restaurantRanking.length === 0 ? (
            <div className="p-12 text-center text-muted-foreground text-xs">Sem dados no período</div>
          ) : (
            <div className="divide-y divide-border/30">
              {restaurantRanking.map((r, i) => (
                <div key={r.slug} className="flex items-center gap-3 px-5 py-3.5 hover:bg-secondary/20 transition-colors group/row">
                  {/* Position */}
                  <div className="shrink-0 w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold"
                    style={i < 3 ? { backgroundColor: `${MEDAL_COLORS[i]}15`, color: MEDAL_COLORS[i] } : {}}
                  >
                    {i < 3 ? (
                      <Crown className="h-4 w-4" style={{ color: MEDAL_COLORS[i] }} />
                    ) : (
                      <span className="text-muted-foreground/60 text-sm">{i + 1}</span>
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-semibold truncate group-hover/row:text-primary transition-colors">{r.name}</span>
                      <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${r.isOpen ? "bg-emerald-500 shadow-sm shadow-emerald-500/50" : "bg-muted-foreground/30"}`} />
                      <Badge variant="outline" className="text-[8px] h-[18px] px-1.5 capitalize border-border/50">{r.type}</Badge>
                    </div>
                    <p className="text-[9px] text-muted-foreground font-mono mt-0.5">/{r.slug}</p>
                  </div>

                  {/* Metrics */}
                  <div className="flex items-center gap-5 shrink-0">
                    <div className="text-right">
                      <p className="text-[10px] text-muted-foreground/70">Pedidos</p>
                      <p className="text-xs font-bold tabular-nums">{r.orderCount.toLocaleString("pt-BR")}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-[10px] text-muted-foreground/70">Hoje</p>
                      <p className="text-xs font-bold text-primary tabular-nums">{r.todayOrders}</p>
                    </div>
                    <div className="text-right min-w-[85px]">
                      <p className="text-[10px] text-muted-foreground/70">Faturamento</p>
                      <p className="text-xs font-bold tabular-nums">{formatCurrency(r.revenue)}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Orders Bar + Recent Orders stacked */}
        <div className="lg:col-span-2 space-y-4">
          {/* Bar chart */}
          <div className="rounded-2xl border border-border/60 bg-card p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-display font-bold text-sm">Pedidos — 7 dias</h3>
                <p className="text-[11px] text-muted-foreground mt-0.5">Volume diário</p>
              </div>
              <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                <Clock className="h-4 w-4 text-emerald-500" />
              </div>
            </div>
            <ResponsiveContainer width="100%" height={140}>
              <BarChart data={revenueChartData}>
                <XAxis dataKey="label" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} dy={4} />
                <YAxis hide />
                <Tooltip contentStyle={tooltipStyle} formatter={(value: number) => [value, "Pedidos"]} />
                <Bar dataKey="orders" fill="hsl(24, 95%, 53%)" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Recent orders */}
          <div className="rounded-2xl border border-border/60 bg-card overflow-hidden">
            <div className="p-5 pb-3 flex items-center justify-between">
              <div>
                <h3 className="font-display font-bold text-sm">Últimos Pedidos</h3>
                <p className="text-[11px] text-muted-foreground mt-0.5">Atividade recente</p>
              </div>
              <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <Eye className="h-4 w-4 text-blue-500" />
              </div>
            </div>
            {recentOrders.length === 0 ? (
              <div className="p-10 text-center text-muted-foreground">
                <ClipboardList className="h-8 w-8 mx-auto mb-2 opacity-15" />
                <p className="text-xs">Nenhum pedido ainda</p>
              </div>
            ) : (
              <div className="divide-y divide-border/30">
                {recentOrders.map((o: any) => {
                  const rest = restaurants.find((r: any) => r.id === o.restaurant_id);
                  return (
                    <div key={o.id} className="flex items-center justify-between px-5 py-3 hover:bg-secondary/20 transition-colors">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] font-mono text-muted-foreground/60">#{o.id.slice(0, 6)}</span>
                          <span className="text-xs font-semibold truncate">{rest?.name || "—"}</span>
                        </div>
                        <p className="text-[10px] text-muted-foreground mt-0.5">{o.customer_name || "Cliente anônimo"}</p>
                      </div>
                      <div className="text-right shrink-0 ml-3">
                        <p className="text-xs font-bold tabular-nums">{formatCurrency(Number(o.total))}</p>
                        <Badge variant="outline" className="text-[9px] capitalize mt-0.5 border-border/50">
                          {STATUS_LABELS[o.status] || o.status}
                        </Badge>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default AdminOverviewTab;
